import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cmsApiConfig } from '../../cmsConfig';
import {
  CreateUserModel,
  AdministrationPageTexts,
  PortalUserListItem,
  SubcontractorDocumentListItem,
  SubcontractoreFileItem,
} from '../../types/Administration';
import {
  ConversionCoachStatusText,
  ConversionProjectListItem,
  ConversionStatusCodeText,
} from '../../types/ConversionProject';
import {
  ParticipationListItem,
  StatusForCoachText,
} from '../../types/Coach/ParticipationListItem';
import { DecisionsOnPackagesTexts } from '../../types/Enums/DecisionOnPackages';
import { ApplicationFile } from '../../types/Application/application';

export const administratorApi = createApi({
  reducerPath: 'administratorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: cmsApiConfig.url + '/administrator',
    headers: { 'X-CSRF': '1' },
    credentials: 'include',
  }),
  tagTypes: ['Users', 'SubcontractorDocuments'],
  endpoints: (builder) => ({
    getAdministrationTexts: builder.query<AdministrationPageTexts, void>({
      query: () => ({
        url: `/getPageTexts`,
      }),
    }),
    getNewProjects: builder.query<ConversionProjectListItem[], void>({
      query: () => ({
        url: `/newProjects`,
      }),
      transformResponse: (response: ConversionProjectListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            statusCodeText:
              ConversionStatusCodeText[
                item.statusCode as keyof typeof ConversionStatusCodeText
              ],
            coachStatusText:
              ConversionCoachStatusText[
                item.coachStatus as keyof typeof ConversionCoachStatusText
              ],
          } as ConversionProjectListItem;
        });
      },
    }),
    getOngoingProjects: builder.query<ConversionProjectListItem[], void>({
      query: () => ({
        url: `/ongoingProjects`,
      }),
      transformResponse: (response: ConversionProjectListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            statusCodeText:
              ConversionStatusCodeText[
                item.statusCode as keyof typeof ConversionStatusCodeText
              ],
            coachStatusText:
              ConversionCoachStatusText[
                item.coachStatus as keyof typeof ConversionCoachStatusText
              ],
          } as ConversionProjectListItem;
        });
      },
    }),
    getFinishedProjects: builder.query<ConversionProjectListItem[], void>({
      query: () => ({
        url: `/finishedProjects`,
      }),
      transformResponse: (response: ConversionProjectListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            statusCodeText:
              ConversionStatusCodeText[
                item.statusCode as keyof typeof ConversionStatusCodeText
              ],
            coachStatusText:
              ConversionCoachStatusText[
                item.coachStatus as keyof typeof ConversionCoachStatusText
              ],
          } as ConversionProjectListItem;
        });
      },
    }),
    getParticipants: builder.query<ParticipationListItem[], void>({
      query: () => ({
        url: `/participants`,
      }),
      transformResponse: (response: ParticipationListItem[]) => {
        return response.map((item) => {
          const decisionOnPackage = item.decisionOnPackage
            ? item.decisionOnPackage
            : undefined;
          const decisionOnPackageText = decisionOnPackage
            ? decisionOnPackage
                .map(
                  (packageDecision) =>
                    DecisionsOnPackagesTexts[
                      packageDecision as keyof typeof DecisionsOnPackagesTexts
                    ]
                )
                .join(', ')
            : undefined;
          return {
            ...item,
            statusForCoachText:
              StatusForCoachText[
                item.statusForCoach as keyof typeof StatusForCoachText
              ],
            decisionOnPackageText: decisionOnPackageText,
          } as ParticipationListItem;
        });
      },
    }),
    getFinishedParticipants: builder.query<ParticipationListItem[], void>({
      query: () => ({
        url: `/finishedParticipants`,
      }),
      transformResponse: (response: ParticipationListItem[]) => {
        return response.map((item) => {
          const decisionOnPackage = item.decisionOnPackage
            ? item.decisionOnPackage
            : undefined;
          const decisionOnPackageText = decisionOnPackage
            ? decisionOnPackage
                .map(
                  (packageDecision) =>
                    DecisionsOnPackagesTexts[
                      packageDecision as keyof typeof DecisionsOnPackagesTexts
                    ]
                )
                .join(', ')
            : undefined;
          return {
            ...item,
            statusForCoachText:
              StatusForCoachText[
                item.statusForCoach as keyof typeof StatusForCoachText
              ],
            decisionOnPackageText: decisionOnPackageText,
          } as ParticipationListItem;
        });
      },
    }),
    getCoaches: builder.query<PortalUserListItem[], void>({
      query: () => ({
        url: `/getCoaches`,
      }),
      providesTags: ['Users'],
      transformResponse: (response: PortalUserListItem[]) => {
        return response.map((item) => {
          return {
            ...item,
            latestCvUpload: item.userDocument?.timeLastModified ?? null,
          } as PortalUserListItem;
        });
      },
    }),
    editUser: builder.mutation<any, PortalUserListItem>({
      query: (editedUser) => ({
        url: `/editUser`,
        method: 'PUT',
        body: editedUser,
      }),
      invalidatesTags: ['Users'],
    }),
    inactivateUser: builder.mutation<any, string>({
      query: (id) => ({
        url: `/inactivateUser/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Users'],
    }),
    addUser: builder.mutation<any, CreateUserModel>({
      query: (userToAdd) => ({
        url: `/addUser/`,
        method: 'POST',
        body: userToAdd,
      }),
      invalidatesTags: ['Users'],
    }),
    uploadCvToUser: builder.mutation<
      any,
      { id: string; fileObj: ApplicationFile }
    >({
      query: ({ id, fileObj }) => ({
        url: `/uploadCv/${id}`,
        method: 'POST',
        body: fileObj,
      }),
      invalidatesTags: ['Users'],
    }),
    getSubcontractorDocuments: builder.query<
      SubcontractorDocumentListItem[],
      void
    >({
      query: () => ({
        url: `/subcontractorDocuments`,
        method: 'GET',
        cache: 'no-cache',
      }),
      providesTags: ['SubcontractorDocuments'],
    }),
    uploadSubcontractorDocument: builder.mutation<
      any,
      { uploadedByUser: string; file: SubcontractoreFileItem }
    >({
      query: (fileItem) => ({
        url: `/uploadSubcontractorDocument?uploadedByUser=${fileItem.uploadedByUser}`,
        method: 'POST',
        body: fileItem.file,
      }),
      invalidatesTags: ['SubcontractorDocuments'],
    }),
    removeSubcontractorDocument: builder.mutation<any, string>({
      query: (id) => ({
        url: `/removeSubcontractorDocument/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubcontractorDocuments'],
    }),
    removeUserDocument: builder.mutation<
      any,
      { contactId: string; documentId: string }
    >({
      query: ({ contactId, documentId }) => ({
        url: `/removeUserDocument?contactId=${contactId}&documentId=${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
  }),
});

export const {
  useLazyGetAdministrationTextsQuery,
  useLazyGetNewProjectsQuery,
  useLazyGetOngoingProjectsQuery,
  useLazyGetFinishedProjectsQuery,
  useLazyGetParticipantsQuery,
  useLazyGetFinishedParticipantsQuery,
  useLazyGetCoachesQuery,
  useEditUserMutation,
  useInactivateUserMutation,
  useAddUserMutation,
  useUploadCvToUserMutation,
  useLazyGetSubcontractorDocumentsQuery,
  useUploadSubcontractorDocumentMutation,
  useRemoveSubcontractorDocumentMutation,
  useRemoveUserDocumentMutation,
} = administratorApi;
